/**
 * <hls-video> — progressive enhancement for a <video> that carries `data-hls-src`
 * (a Shopify-generated HLS `.m3u8` URL) for adaptive-bitrate playback.
 *
 * Safari/iOS play HLS natively (0 KB JS): we just point the video at the m3u8.
 * Everywhere else we lazy-load the vendored hls.js (~50 KB gzip) — ONLY when such
 * an element is on the page — and attach it. If hls.js can't run, the inner
 * <video>'s own mp4 <source> is the progressive fallback.
 *
 * Emitted by snippets/video.liquid only when `video_hls` is true and the uploaded
 * video exposes an m3u8 source. Works inline and inside <deferred-media> (the cloned
 * node's connectedCallback fires on insertion, so no hook into media.js is needed).
 */
class HlsVideo extends HTMLElement {
  /** @type {import('@theme/hls').default | null} */
  #hls = null;

  connectedCallback() {
    const video = this.querySelector('video');
    const src = this.dataset.hlsSrc;
    // Guard against a second connect (e.g. re-insertion) re-attaching.
    if (!(video instanceof HTMLVideoElement) || !src || this.dataset.hlsReady) return;
    this.dataset.hlsReady = 'true';
    this.#setup(video, src);
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {string} src
   */
  async #setup(video, src) {
    // iPhone Safari has native HLS but no MediaSource → play natively, load nothing.
    // NOTE: we must NOT native-first via canPlayType: Chrome returns a false-positive
    // "maybe" for HLS yet can't actually play it. So we gate on MediaSource instead —
    // present in Chrome/Firefox/Edge/desktop-Safari/iPad (use hls.js), absent on iPhone.
    if (!('MediaSource' in window)) {
      if (video.canPlayType('application/vnd.apple.mpegurl')) video.src = src;
      return;
    }

    try {
      const { default: Hls } = await import('@theme/hls');
      if (!this.isConnected) return; // removed while loading
      if (Hls.isSupported()) {
        // Cap the rendition to the player size so a small player never pulls 1080p.
        const hls = new Hls({ capLevelToPlayerSize: true });
        hls.loadSource(src);
        hls.attachMedia(video);
        this.#hls = hls;
        return;
      }
      // MSE present but hls.js unsupported (rare) → try native, else mp4 <source>.
      if (video.canPlayType('application/vnd.apple.mpegurl')) video.src = src;
    } catch {
      // Import/parse failure → the inner mp4 <source> fallback handles playback.
    }
  }

  disconnectedCallback() {
    this.#hls?.destroy();
    this.#hls = null;
  }
}

if (!customElements.get('hls-video')) {
  customElements.define('hls-video', HlsVideo);
}
